import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Badge, Form } from 'react-bootstrap';
import moment from 'moment';
import Swal from 'sweetalert2';
import { ServiceData } from './Service';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import apidefinitions from '../../../../core/api/apiDefinitions';
import ViewServiceModal from './ViewServiceModal'; // Adjust path as needed

interface PendingApprovedModuleProps {
  show: boolean;
  onHide: () => void;
  statusColors: { [key: string]: string };
}

const PendingApprovedModule: React.FC<PendingApprovedModuleProps> = ({ show, onHide, statusColors }) => {
  const [services, setServices] = useState<ServiceData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [rejectServiceId, setRejectServiceId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [rejectError, setRejectError] = useState<string | null>(null);

  // Fetch services with PENDING approval status when the modal is shown
  useEffect(() => {
    if (show && checkPermission('ROLE_SERVICE')) {
      fetchServices();
    }
  }, [show]);

  const fetchServices = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        filterType: 'APPROVAL_STATUS',
        filterValue: 'PENDING_APPROVAL',
        date: '',
      };
      const response = await apidefinitions.getVehiclesServiceData(1, payload);
      const fetchedServices: ServiceData[] = response.data.data.vehiclesServiceData || [];
      setServices(fetchedServices);
    } catch (err) {
      setError('Failed to load pending approval services. Please try again.');
      Swal.fire({
        icon: 'error',
        title: 'Fetch Failed',
        text: 'Failed to load pending approval services.',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-4 py-2' },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleView = (serviceId: number) => {
    setSelectedServiceId(serviceId);
    setShowViewModal(true);
  };

  const handleApprove = async (serviceId: number) => {
    Swal.fire({
      icon: 'question',
      title: 'Confirm Approval',
      text: 'Are you sure you want to approve this service?',
      showCancelButton: true,
      confirmButtonText: 'Approve',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-4 py-2',
        cancelButton: 'bg-gray-300 text-gray-700 hover:bg-gray-400 rounded-lg px-4 py-2',
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const payload = { actionType: 'approve' };
          await apidefinitions.updateVehicleServiceApprovalStatus(serviceId, payload);
          Swal.fire({
            icon: 'success',
            title: 'Approved',
            text: 'Service has been approved successfully.',
            confirmButtonText: 'OK',
            customClass: { confirmButton: 'bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-4 py-2' },
          });
          fetchServices(); // Refresh the list
        } catch (err: any) {
          Swal.fire({
            icon: 'error',
            title: 'Approval Failed',
            text: err.response?.data?.statusDescription || 'Failed to approve service.',
            confirmButtonText: 'OK',
            customClass: { confirmButton: 'bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-4 py-2' },
          });
        }
      }
    });
  };

  const handleReject = (serviceId: number) => {
    setRejectServiceId(serviceId);
    setRejectReason('');
    setRejectError(null);
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      setRejectError('You must provide a reason for rejection!');
      return;
    }
    if (rejectServiceId === null) return;

    try {
      const payload = { actionType: 'reject', reason: rejectReason.trim() };
      await apidefinitions.updateVehicleServiceApprovalStatus(rejectServiceId, payload);
      Swal.fire({
        icon: 'success',
        title: 'Rejected',
        text: 'Service has been rejected successfully.',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-4 py-2' },
      });
      setShowRejectModal(false);
      setRejectReason('');
      setRejectServiceId(null);
      fetchServices(); // Refresh the list
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Rejection Failed',
        text: err.response?.data?.statusDescription || 'Failed to reject service.',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-4 py-2' },
      });
    }
  };

  const handleRejectModalClose = () => {
    setShowRejectModal(false);
    setRejectReason('');
    setRejectError(null);
    setRejectServiceId(null);
  };

  return (
    <>
      <Modal
        show={show && checkPermission('ROLE_SERVICE')}
        onHide={onHide}
        size="xl"
        centered
        className="rounded-xl shadow-2xl"
      >
        <Modal.Header closeButton className="bg-teal-600 text-white border-b-0 rounded-t-xl">
          <Modal.Title className="text-xl font-semibold">Pending Approval Services</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6 bg-white">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-600 mr-2"></div>
              Loading...
            </div>
          ) : error ? (
            <div className="text-center text-danger bg-red-50 p-4 rounded-lg">{error}</div>
          ) : (
            <div className="table-responsive">
              <Table className="table text-nowrap table-striped">
                <thead className="table-primary">
                  <tr>
                    <th>ID</th>
                    <th>Vehicle ID</th>
                    <th>Service Type</th>
                    <th>Service KMs</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {services.length > 0 ? (
                    services.map((service) => (
                      <tr key={service.id}>
                        <td>{service.id}</td>
                        <td>{service.vehicleId}</td>
                        <td>{service.serviceType}</td>
                        <td>{service.serviceKms}</td>
                        <td>
                          <Badge bg={statusColors[service.serviceStatus || ''] || 'secondary'}>
                            {service.serviceStatus || 'N/A'}
                          </Badge>
                        </td>
                        <td>{moment(service.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                        <td>
                          <div className="hstack gap-2 fs-5">
                            {/* View button */}
                            <Button
                              variant="icon btn-secondary rounded-circle btn-sm"
                              onClick={() => handleView(service.id)}
                              title="View Service"
                            >
                              <i className="feather-eye" />
                            </Button>

                            {/* Approve & Reject only if approvalStatus is PENDING */}
                            {service.approvalStatus === 'PENDING_APPROVAL' && checkPermission('ROLE_SERVICE') && (
                              <>
                                <Button
                                  variant="icon btn-success rounded-circle btn-sm"
                                  onClick={() => handleApprove(service.id)}
                                  title="Approve Service"
                                >
                                  <i className="feather-check" />
                                </Button>

                                <Button
                                  variant="icon btn-danger rounded-circle btn-sm"
                                  onClick={() => handleReject(service.id)}
                                  title="Reject Service"
                                >
                                  <i className="feather-x" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        No pending approval services found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
         <Modal.Footer className="bg-light">
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Reason Modal */}
      <Modal
        show={showRejectModal}
        onHide={handleRejectModalClose}
        centered
        className="rounded-xl shadow-2xl"
      >
        <Modal.Header closeButton className="bg-teal-600 text-white border-b-0 rounded-t-xl">
          <Modal.Title className="text-lg font-semibold">Reject Service</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6 bg-white">
          <Form>
            <Form.Group controlId="rejectReason">
              <Form.Label className="text-sm font-medium text-gray-700">Reason for Rejection</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={rejectReason}
                onChange={(e) => {
                  setRejectReason(e.target.value);
                  setRejectError(null); // Clear error on input change
                }}
                placeholder="Enter reason here..."
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-teal-600 focus:border-teal-600"
              />
              {rejectError && (
                <Form.Text className="text-danger">{rejectError}</Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-t-0 rounded-b-xl p-4">
          <Button
            variant="outline-primary"
            onClick={handleRejectModalClose}
            className="border-gray-300 text-gray-700 hover:bg-gray-200 hover:text-gray-800 rounded-lg px-4 py-2"
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handleRejectSubmit}
            className="bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-4 py-2"
          >
            Reject
          </Button>
        </Modal.Footer>
      </Modal>

      <ViewServiceModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        serviceId={selectedServiceId}
        statusColors={statusColors}
      />
    </>
  );
};

export default PendingApprovedModule;